import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import WarningIcon from "@material-ui/icons/ReportProblemOutlined";
import { Skeleton } from "@material-ui/lab";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import QueueModal from "../../components/QueueModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import EmptyState from "../../components/EmptyState";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { socketConnection } from "../../services/socket";
import stringToColor from "../../helpers/stringToColor";
import { moveQueue, queueHoursStatus, sortQueues } from "../../helpers/queueHours";

// Mesmos números que o backend usa no menu do WhatsApp.
const MENU_NUMS = ["1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣", "🔟"];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const chip = (bg, fg) => ({
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 500,
    backgroundColor: bg,
    color: fg,
  });
  return {
    header: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(0, 0.75, 2),
      color: theme.palette.text.primary,
    },
    headerTitle: {
      minWidth: 0,
      "& h5": { marginBottom: 0, fontWeight: 600 },
    },
    muted: { color: theme.palette.text.secondary },
    headerButtons: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        "& > *": { flex: "1 1 auto" },
      },
    },
    headerButton: {
      textTransform: "none",
      fontWeight: 600,
      borderRadius: 10,
      height: 40,
      padding: theme.spacing(0, 2),
    },
    secondaryButton: {
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.background.paper,
    },
    body: {
      flex: 1,
      minHeight: 0,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 300px",
      gap: theme.spacing(2),
      alignItems: "start",
      overflowY: "auto",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    list: {
      borderRadius: 14,
      overflow: "hidden",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "40px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.4fr) auto",
      gap: theme.spacing(1.75),
      alignItems: "center",
      padding: theme.spacing(1.75, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      "&:first-child": { borderTop: 0 },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "34px minmax(0, 1fr) auto",
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.5),
      },
    },
    headRow: {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    pos: {
      width: 32,
      height: 32,
      borderRadius: 8,
      display: "grid",
      placeItems: "center",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: dark ? "rgba(255,255,255,0.04)" : "#f5f6f8",
    },
    name: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 600,
      fontSize: 15,
      minWidth: 0,
      "& span:last-child": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    dot: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      flex: "none",
    },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 4,
    },
    chipBot: chip(dark ? "#004d40" : "#e0f2f1", dark ? "#80cbc4" : "#00796b"),
    chipAI: chip(dark ? "#4a148c" : "#ede7f6", dark ? "#ce93d8" : "#6a1b9a"),
    chipOpen: chip(dark ? "#1b5e20" : "#e8f5e9", dark ? "#a5d6a7" : "#2e7d32"),
    chipClosed: chip(dark ? "#5c1a1a" : "#ffebee", dark ? "#ef9a9a" : "#c62828"),
    team: {
      display: "grid",
      gap: 3,
      minWidth: 0,
    },
    avatars: {
      display: "flex",
      "& > *": {
        width: 26,
        height: 26,
        fontSize: 10,
        fontWeight: 700,
        border: `2px solid ${theme.palette.background.paper}`,
        marginLeft: -6,
      },
      "& > *:first-child": { marginLeft: 0 },
    },
    meta: {
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    warn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      maxWidth: "100%",
      width: "max-content",
      fontSize: 12,
      fontWeight: 500,
      padding: "2px 8px",
      borderRadius: 8,
      color: dark ? "#f3c969" : "#8a5a00",
      backgroundColor: dark ? "#3d3218" : "#fff6db",
      "& svg": { fontSize: 15 },
    },
    greeting: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      wordBreak: "break-word",
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
    },
    hideSmall: {
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    showSmall: {
      display: "none",
      [theme.breakpoints.down("sm")]: { display: "block" },
    },
    preview: {
      borderRadius: 14,
      overflow: "hidden",
      position: "sticky",
      top: 0,
    },
    previewTop: {
      padding: theme.spacing(1.5, 1.75),
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "grid",
      gap: theme.spacing(1),
    },
    waBody: {
      backgroundColor: dark ? "#0b141a" : "#e9e3da",
      padding: theme.spacing(1.75),
    },
    bubble: {
      backgroundColor: dark ? "#202c33" : "#ffffff",
      color: dark ? "#e9edef" : "#111b21",
      borderRadius: "0 10px 10px 10px",
      padding: "8px 10px",
      fontSize: 13.5,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
      maxWidth: "94%",
    },
    previewNote: {
      padding: theme.spacing(1.25, 1.75),
      fontSize: 12,
      color: theme.palette.text.secondary,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    empty: {
      padding: theme.spacing(3),
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
  };
});

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

// Prévia simples das variáveis mais usadas na saudação da conexão.
const fillGreeting = (text = "") =>
  text
    .replace(/{{\s*firstName\s*}}/g, "Maria")
    .replace(/{{\s*name\s*}}/g, "Maria Souza")
    .replace(/{{\s*(ms|gretting)\s*}}/g, "Bom dia");

// Negrito do WhatsApp (*texto*) na prévia.
const renderWhatsText = (text) =>
  text.split(/(\*[^*\n]+\*)/g).map((part, i) =>
    part.length > 2 && part.startsWith("*") && part.endsWith("*") ? (
      <strong key={i}>{part.slice(1, -1)}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

const QueueRow = ({ queue, position, total, hoursEnabled, now, classes, onEdit, onMove, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const users = queue.users || [];
  const whatsapps = queue.whatsapps || [];
  const hours = hoursEnabled ? queueHoursStatus(queue, now) : null;
  const teamLoaded = Array.isArray(queue.users);

  const closeMenu = () => setAnchorEl(null);

  const teamInfo = teamLoaded ? (
    <div className={classes.team}>
      {users.length === 0 ? (
        <>
          <span className={classes.warn}>
            <WarningIcon /> Nenhum atendente
          </span>
          <span className={classes.meta}>Só o admin vê estas conversas</span>
        </>
      ) : (
        <>
          <div className={classes.avatars}>
            {users.slice(0, 4).map((u) => (
              <Tooltip key={u.id} title={u.name}>
                <Avatar style={{ backgroundColor: stringToColor(u.name || String(u.id)) }}>
                  {initials(u.name)}
                </Avatar>
              </Tooltip>
            ))}
            {users.length > 4 && <Avatar>+{users.length - 4}</Avatar>}
          </div>
          <span className={classes.meta}>
            {users.length} {users.length === 1 ? "atendente" : "atendentes"} · {whatsapps.length}{" "}
            {whatsapps.length === 1 ? "conexão" : "conexões"}
          </span>
        </>
      )}
    </div>
  ) : (
    <div />
  );

  return (
    <div className={classes.row}>
      <Tooltip title="Posição no menu do WhatsApp">
        <span className={classes.pos}>{position}</span>
      </Tooltip>

      <div style={{ minWidth: 0 }}>
        <div className={classes.name}>
          <span className={classes.dot} style={{ backgroundColor: queue.color }} />
          <span>{queue.name}</span>
        </div>
        {(queue.integrationId || queue.promptId || hours) && (
          <div className={classes.chips}>
            {queue.integrationId && <Chip size="small" label="Bot" className={classes.chipBot} />}
            {queue.promptId && <Chip size="small" label="IA" className={classes.chipAI} />}
            {hours && (
              <Chip
                size="small"
                label={hours.label}
                className={hours.open ? classes.chipOpen : classes.chipClosed}
              />
            )}
          </div>
        )}
        {/* No celular a equipe aparece embaixo do nome. */}
        <div className={classes.showSmall} style={{ marginTop: 6 }}>
          {teamInfo}
        </div>
      </div>

      <div className={classes.hideSmall}>{teamInfo}</div>

      <Tooltip title={queue.greetingMessage || ""} placement="top-start">
        <div className={`${classes.greeting} ${classes.hideSmall}`}>
          {queue.greetingMessage || <em>Sem saudação</em>}
        </div>
      </Tooltip>

      <div className={classes.actions}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(queue)} aria-label="Editar">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Mais ações">
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem
            disabled={position === 1}
            onClick={() => {
              closeMenu();
              onMove(queue, -1);
            }}
          >
            <ListItemIcon>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mover para cima" />
          </MenuItem>
          <MenuItem
            disabled={position === total}
            onClick={() => {
              closeMenu();
              onMove(queue, 1);
            }}
          >
            <ListItemIcon>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mover para baixo" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeMenu();
              onDelete(queue);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Excluir" />
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

const MenuPreview = ({ classes, queues, whatsapps }) => {
  const [whatsappId, setWhatsappId] = useState("");

  useEffect(() => {
    if (whatsapps.length === 0) return;
    if (!whatsapps.some((w) => w.id === whatsappId)) {
      const preferred = whatsapps.find((w) => w.isDefault) || whatsapps[0];
      setWhatsappId(preferred.id);
    }
  }, [whatsapps, whatsappId]);

  const whatsapp = whatsapps.find((w) => w.id === whatsappId);
  const linkedIds = new Set((whatsapp?.queues || []).map((q) => q.id));
  const menuQueues = queues.filter((q) => linkedIds.has(q.id));

  let content;
  let note = "A ordem do menu é a da lista. A saudação vem da conexão.";

  if (!whatsapp) {
    content = <div className={classes.empty}>Nenhuma conexão cadastrada.</div>;
    note = null;
  } else if (menuQueues.length === 0) {
    content = (
      <div className={classes.empty}>
        Esta conexão não tem departamento vinculado. As conversas dela não são direcionadas a ninguém.
      </div>
    );
    note = "Vincule departamentos no cadastro da conexão.";
  } else if (menuQueues.length === 1) {
    content = (
      <div className={classes.empty}>
        Com um só departamento o cliente não vê menu: a conversa vai direto para{" "}
        <strong>{menuQueues[0].name}</strong>.
      </div>
    );
    note = null;
  } else {
    const options = menuQueues
      .map((q, i) => `${i < MENU_NUMS.length ? MENU_NUMS[i] : `*[${i + 1}]*`} ${q.name}`)
      .join("\n");
    const text = `${fillGreeting(whatsapp.greetingMessage || "")}\n\n${options}\n\n🏠 *#* Início`.trim();
    content = (
      <div className={classes.waBody}>
        <div className={classes.bubble}>{renderWhatsText(text)}</div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.previewTop}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          Como o cliente vê
        </Typography>
        {whatsapps.length > 0 && (
          <TextField
            select
            size="small"
            variant="outlined"
            id="queue-preview-whatsapp"
            label="Conexão"
            value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
          >
            {whatsapps.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </div>
      {content}
      {note && <div className={classes.previewNote}>{note}</div>}
    </>
  );
};

const Queues = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueToDelete, setQueueToDelete] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [moving, setMoving] = useState(false);

  const sorted = useMemo(() => sortQueues(queues), [queues]);

  const loadQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/queue", { params: { withTeam: true } });
      setQueues(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadWhatsapps = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/", { params: { session: 0 } });
      setWhatsapps(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        loadQueues(),
        loadWhatsapps(),
        api
          .get("/settings")
          .then(({ data }) => {
            const scheduleType = Array.isArray(data) && data.find((d) => d.key === "scheduleType");
            setHoursEnabled(scheduleType?.value === "queue");
          })
          .catch(() => {}),
      ]);
      setLoading(false);
    })();
  }, [loadQueues, loadWhatsapps]);

  // O chip "Aberto até / Fechado" acompanha o relógio.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Mudança em departamento, usuário ou conexão recarrega a lista, que já
  // vem ordenada e com a equipe. Agrupa rajadas (ex.: ao mover).
  const reloadTimer = useRef(null);
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    const scheduleReload = (fn) => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(fn, 300);
    };
    socket.on(`company-${companyId}-queue`, () => scheduleReload(loadQueues));
    socket.on(`company-${companyId}-user`, () => scheduleReload(loadQueues));
    socket.on(`company-${companyId}-whatsapp`, () =>
      scheduleReload(() => {
        loadQueues();
        loadWhatsapps();
      })
    );
    return () => {
      clearTimeout(reloadTimer.current);
      socket.disconnect();
    };
  }, [loadQueues, loadWhatsapps]);

  const handleNew = () => {
    setSelectedQueue(null);
    setQueueModalOpen(true);
  };

  const handleEdit = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
    loadQueues();
  };

  const handleMove = async (queue, direction) => {
    if (moving) return;
    const changes = moveQueue(sorted, queue.id, direction);
    if (changes.length === 0) return;
    setMoving(true);
    // Mostra a nova ordem na hora; o backend confirma em seguida.
    const newOrder = new Map(changes.map((c) => [c.queue.id, c.orderQueue]));
    setQueues((prev) =>
      prev.map((q) => (newOrder.has(q.id) ? { ...q, orderQueue: newOrder.get(q.id) } : q))
    );
    try {
      for (const { queue: q, orderQueue } of changes) {
        // O update exige os campos principais; manda o departamento inteiro.
        // eslint-disable-next-line no-await-in-loop
        await api.put(`/queue/${q.id}`, {
          name: q.name,
          color: q.color,
          greetingMessage: q.greetingMessage,
          outOfHoursMessage: q.outOfHoursMessage,
          schedules: q.schedules,
          orderQueue,
          integrationId: q.integrationId ?? null,
          promptId: q.promptId ?? null,
        });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setMoving(false);
      loadQueues();
    }
  };

  const handleDelete = async () => {
    const queue = queueToDelete;
    setQueueToDelete(null);
    if (!queue) return;
    try {
      await api.delete(`/queue/${queue.id}`);
      toast.success("Departamento excluído.");
      setQueues((prev) => prev.filter((q) => q.id !== queue.id));
    } catch (error) {
      if (error.response?.data?.error) {
        toastError(error);
      } else if (error.response?.status === 409) {
        toastError("Não foi possível excluir: há um prompt de IA ou uma integração usando este departamento.");
      } else {
        toastError(i18n.t("queues.errors.delete"));
      }
    }
  };

  const withoutTeam = sorted.filter((q) => Array.isArray(q.users) && q.users.length === 0).length;
  const countLabel = loading
    ? ""
    : [
        `${sorted.length} ${sorted.length === 1 ? "departamento" : "departamentos"}`,
        withoutTeam > 0 && `${withoutTeam} sem atendente`,
        "o cliente escolhe pelo menu do WhatsApp",
      ]
        .filter(Boolean)
        .join(" · ");

  const maxOrder = sorted.reduce((max, q) => Math.max(max, Number(q.orderQueue) || 0), 0);

  return (
    <MainContainer>
      <ConfirmationModal
        title={queueToDelete ? `${i18n.t("queues.confirmationModal.deleteTitle")} ${queueToDelete.name}?` : ""}
        open={Boolean(queueToDelete)}
        onClose={() => setQueueToDelete(null)}
        onConfirm={handleDelete}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseModal}
        queueId={selectedQueue?.id}
        defaultOrder={maxOrder + 1}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Menu do WhatsApp</DialogTitle>
        <DialogContent style={{ padding: 0 }}>
          <MenuPreview classes={classes} queues={sorted} whatsapps={whatsapps} />
        </DialogContent>
      </Dialog>

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>{i18n.t("queues.title")}</Title>
          <Typography variant="body2" className={classes.muted}>
            {countLabel || " "}
          </Typography>
        </div>
        <div className={classes.headerButtons}>
          {isSmall && sorted.length > 0 && (
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<WhatsAppIcon />}
              onClick={() => setPreviewOpen(true)}
            >
              Ver menu
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.body}>
        <Paper variant="outlined" className={classes.list}>
          {!isSmall && sorted.length > 0 && (
            <div className={`${classes.row} ${classes.headRow}`}>
              <span>Menu</span>
              <span>Departamento</span>
              <span>Equipe</span>
              <span>Saudação</span>
              <span />
            </div>
          )}

          {loading ? (
            [0, 1, 2].map((i) => (
              <div key={i} className={classes.row}>
                <Skeleton variant="rect" width={32} height={32} style={{ borderRadius: 8 }} />
                <Skeleton width="60%" />
                <Skeleton width="50%" className={classes.hideSmall} />
                <Skeleton width="80%" className={classes.hideSmall} />
                <span />
              </div>
            ))
          ) : sorted.length === 0 ? (
            <EmptyState
              icon="🗂️"
              title="Nenhum departamento criado"
              description="Crie departamentos para organizar o atendimento por área (ex.: Vendas, Suporte, Financeiro). O cliente escolhe o departamento pelo menu do WhatsApp."
              actionLabel="+ Criar departamento"
              onAction={handleNew}
            />
          ) : (
            sorted.map((queue, index) => (
              <QueueRow
                key={queue.id}
                queue={queue}
                position={index + 1}
                total={sorted.length}
                hoursEnabled={hoursEnabled}
                now={now}
                classes={classes}
                onEdit={handleEdit}
                onMove={handleMove}
                onDelete={setQueueToDelete}
              />
            ))
          )}
        </Paper>

        {!isSmall && !loading && sorted.length > 0 && (
          <Paper variant="outlined" className={classes.preview}>
            <MenuPreview classes={classes} queues={sorted} whatsapps={whatsapps} />
          </Paper>
        )}
      </div>
    </MainContainer>
  );
};

export default Queues;
